import { esc, showErr } from './users-shared';
import { fetchTeams } from './teams-shared';
import { maskSecret } from '../clicker/secret';
import { openModal, closeModal } from '../ui/modal';
import { renderApiIpSection } from './team-api-ips';
import { t } from '../ui/lang';
/**
 * "API" tab of the Users page (admin and above — gated by `can('tab.teamApi')` at the
 * call site in users.ts).
 *
 * One place for everything a team's own integration needs: the team's API key
 * (generate / revoke, backed by GET+PUT /api/teams/:id/api-key) AND the documentation
 * for the endpoint that key unlocks, deliberately in the same section so an admin
 * handing the key over can copy the usage next to it.
 *
 * Team scope comes from GET /api/teams: an admin gets exactly their own team (so the
 * picker collapses to a single team), a superadmin gets all of them and chooses.
 */
export async function renderTeamApiTab(panel) {
    panel.innerHTML = `<div class="access-loading">${t('common.loading')}</div>`;
    let teams;
    try {
        teams = await fetchTeams();
    }
    catch {
        panel.innerHTML = `<p class="empty">${t('users.failed_to_load_teams')}</p>`;
        return;
    }
    if (teams.length === 0) {
        panel.innerHTML = `<p class="empty">${t('users.no_team_no_api_key')}</p>`;
        return;
    }
    const picker = teams.length > 1
        ? `<div class="modal__field" style="max-width:320px">
               <label for="ta-team">${t('common.team')}</label>
               <select class="settings-input" id="ta-team">
                   ${teams.map((t) => `<option value="${t.id}">${esc(t.name)}</option>`).join('')}
               </select>
           </div>`
        : '';
    panel.innerHTML = `
        ${picker}
        <div id="ta-key-section"></div>
    `;
    const section = panel.querySelector('#ta-key-section');
    const select = panel.querySelector('#ta-team');
    const currentTeam = () => teams.find((t) => t.id === Number(select?.value ?? teams[0].id)) ?? teams[0];
    select?.addEventListener('change', () => void renderKeySection(section, currentTeam()));
    wireDocsCopy(section);
    await renderKeySection(section, currentTeam());
}
// ── Key + docs for one team ─────────────────────────────────────────────────────
async function renderKeySection(section, team) {
    section.innerHTML = `<div class="access-loading">${t('common.loading')}</div>`;
    let key = '';
    let ips = [];
    try {
        const res = await fetch(`/api/teams/${team.id}/api-key`);
        if (!res.ok)
            throw new Error('failed');
        const payload = await res.json();
        key = payload.api_key ?? '';
        ips = payload.ips ?? [];
    }
    catch {
        section.innerHTML = `<p class="empty">${t('users.failed_to_load_api_key')}</p>`;
        return;
    }
    // Revealed only on demand — the tab may well be open on a shared screen. The Copy
    // button works either way (it copies the real value, not what is displayed).
    let revealed = false;
    section.innerHTML = `
        <div class="settings-section">
            <h3 class="settings-section__title">${t('users.api_key_title', { name: esc(team.name) })}</h3>
            <p class="settings-hint">
                ${t('users.api_key_hint')}
                ${key ? '' : t('users.api_key_not_set_hint')}
            </p>
            <div class="launch-block">
                <pre class="launch-pre" id="ta-key-value">${key ? esc(maskSecret(key)) : t('users.api_key_not_set')}</pre>
                ${key ? `<button type="button" class="tbl-btn copy-key-btn" id="ta-copy">${t('users.copy_btn')}</button>` : ''}
            </div>
            <div class="users-toolbar" style="margin-top:10px">
                ${key ? `<button type="button" class="tbl-btn" id="ta-reveal">${t('users.show')}</button>` : ''}
                <button type="button" class="tbl-btn" id="ta-generate">${key ? t('users.generate_new_key') : t('users.generate_key')}</button>
                ${key ? `<button type="button" class="tbl-btn" id="ta-revoke">${t('users.revoke')}</button>` : ''}
            </div>
            <div class="settings-error modal__error" id="ta-error" style="display:none"></div>
        </div>
        <div id="ta-ips"></div>
        ${docsHtml(key)}
    `;
    // The whitelist owns its own save cycle (it is edited, not minted), so it renders
    // itself into the placeholder rather than being rebuilt with the key block.
    renderApiIpSection(section.querySelector('#ta-ips'), team, ips);
    const errorEl = section.querySelector('#ta-error');
    const valueEl = section.querySelector('#ta-key-value');
    section.querySelector('#ta-reveal')?.addEventListener('click', (e) => {
        revealed = !revealed;
        valueEl.textContent = revealed ? key : maskSecret(key);
        e.currentTarget.textContent = revealed ? t('users.hide') : t('users.show');
    });
    section.querySelector('#ta-copy')?.addEventListener('click', (e) => {
        const btn = e.currentTarget;
        void navigator.clipboard.writeText(key);
        btn.textContent = t('users.copied_check');
        setTimeout(() => { btn.textContent = t('users.copy_btn'); }, 1500);
    });
    // Generating replaces the stored key, so an existing one is invalidated the moment
    // the new one is saved — confirm first, exactly like the revoke below.
    section.querySelector('#ta-generate')?.addEventListener('click', () => {
        const save = () => void saveKey(section, team, 'generate', errorEl);
        if (!key) {
            save();
            return;
        }
        confirmKeyChange(t('users.confirm_generate_title'), t('users.confirm_generate_text', { name: `<strong>${esc(team.name)}</strong>` }), t('users.generate'), save);
    });
    section.querySelector('#ta-revoke')?.addEventListener('click', () => {
        confirmKeyChange(t('users.confirm_revoke_title'), t('users.confirm_revoke_text', { name: `<strong>${esc(team.name)}</strong>` }), t('users.revoke'), () => void saveKey(section, team, 'revoke', errorEl));
    });
}
/** Mint or clear the key, then re-render the section (which re-reads it from the
 *  server). The key itself is always server-generated — the caller only picks the
 *  action, so there is nothing to validate here. */
async function saveKey(section, team, action, errorEl) {
    errorEl.style.display = 'none';
    try {
        const res = await fetch(`/api/teams/${team.id}/api-key`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ action }),
        });
        if (!res.ok) {
            showErr(errorEl, (await res.json()).error ?? t('users.failed_to_save_key'));
            return;
        }
    }
    catch {
        showErr(errorEl, t('users.network_error'));
        return;
    }
    await renderKeySection(section, team);
}
function confirmKeyChange(title, text, confirmLabel, onConfirm) {
    const modal = openModal(`
        <h2 class="modal__title">${title}</h2>
        <p class="modal__text">${text}</p>
        <div class="modal__actions">
            <button type="button" class="modal__cancel" id="tak-cancel">${t('common.cancel')}</button>
            <button type="button" class="modal__submit modal__submit--danger" id="tak-confirm">${confirmLabel}</button>
        </div>
    `);
    modal.querySelector('#tak-cancel').addEventListener('click', closeModal);
    // The result (including any error) lands in the section's own error line, so the
    // modal just closes and hands off.
    modal.querySelector('#tak-confirm').addEventListener('click', () => {
        closeModal();
        onConfirm();
    });
}
// ── Documentation — same section as the key, per the "hand it over together" rule ──
/** esc() escapes &<> only, which is enough inside an element but not inside an
 *  attribute — the curl example carries double quotes that would close it early. */
function escAttr(s) {
    return esc(s).replace(/"/g, '&quot;');
}
/** The endpoint docs. `key` is only used to make the examples copy-pasteable; when no
 *  key is set the examples fall back to a placeholder. */
function docsHtml(key) {
    const origin = window.location.origin;
    const sample = key || '<your-api-key>';
    const curl = `curl -s "${origin}/api/v1/notifications?period=day" \\\n  -H "X-Api-Key: ${sample}"`;
    const curlShots = `curl -s "${origin}/api/v1/notifications?period=day&screenshots=1" \\\n  -H "X-Api-Key: ${sample}"`;
    const response = `{
  "team":      { "id": 3, "name": "Alpha" },
  "period":    "day",
  "from":      "2026-08-23T09:00:00.000Z",
  "to":        "2026-08-24T09:00:00.000Z",
  "total":     2,
  "truncated": false,
  "farms": {
    "12": {
      "id": 12,
      "name": "Warsaw-1",
      "operations": [
        {
          "id":              9871,
          "udid":            "R58N12ABCDE",
          "tx_id":           "884213",
          "task_type":       "merchant_payout",
          "app_name":        "PersonalPay",
          "amount":          "15000",
          "currency":        "ARS",
          "account_from":    "1234567890",
          "account_to":      "0987654321",
          "status":          "success",
          "person_from":     "Juan Perez",
          "person_to":       "Maria Gomez",
          "operation_at":    "2026-08-24 05:40:55",
          "balance":         "482310.55",
          "comment":         "",
          "operator_login":  "kitten_dots",
          "platform_synced": true,
          "tg_synced":       true,
          "created_at":      "2026-08-24T08:41:02.117Z",
          "has_screenshot":  false
        }
      ]
    },
    "17": { "id": 17, "name": "Warsaw-2", "operations": [] }
  }
}`;
    return `
        <div class="settings-section">
            <h3 class="settings-section__title">${t('users.api_docs_title')}</h3>
            <p class="settings-hint">
                ${t('users.api_docs_intro')}
            </p>

            <p class="settings-hint"><strong>${t('users.api_docs_endpoint')}</strong></p>
            <div class="launch-block">
                <pre class="launch-pre">GET ${esc(origin)}/api/v1/notifications?period=day</pre>
            </div>

            <p class="settings-hint" style="margin-top:12px"><strong>${t('users.api_docs_auth')}</strong> — ${t('users.api_docs_auth_text')}</p>
            <div class="launch-block">
                <pre class="launch-pre">X-Api-Key: ${esc(sample)}
Authorization: Bearer ${esc(sample)}</pre>
            </div>
            <p class="settings-hint">${t('users.api_docs_401')}</p>
            <p class="settings-hint">
                ${t('users.api_docs_403')}
            </p>

            <p class="settings-hint" style="margin-top:12px"><strong>${t('users.api_docs_query_params')}</strong></p>
            <ul class="settings-hint" style="padding-left:18px;line-height:1.7">
                <li>${t('users.api_docs_period')}</li>
                <li>${t('users.api_docs_screenshots')}</li>
            </ul>

            <p class="settings-hint" style="margin-top:12px"><strong>${t('users.api_docs_example')}</strong></p>
            <div class="launch-block">
                <pre class="launch-pre">${esc(curl)}</pre>
                <button type="button" class="tbl-btn copy-key-btn" data-copy="${escAttr(curl)}">${t('users.copy_btn')}</button>
            </div>

            <div class="launch-block" style="margin-top:8px">
                <pre class="launch-pre">${esc(curlShots)}</pre>
                <button type="button" class="tbl-btn copy-key-btn" data-copy="${escAttr(curlShots)}">${t('users.copy_btn')}</button>
            </div>
            <p class="settings-hint">${t('users.api_docs_screenshots_note')}</p>

            <p class="settings-hint" style="margin-top:12px"><strong>${t('users.api_docs_response')}</strong> <code>200 application/json</code></p>
            <div class="launch-block">
                <pre class="launch-pre">${esc(response)}</pre>
            </div>

            <p class="settings-hint" style="margin-top:12px"><strong>${t('users.api_docs_fields')}</strong></p>
            <ul class="settings-hint" style="padding-left:18px;line-height:1.7">
                <li>${t('users.api_docs_field_farms')}</li>
                <li>${t('users.api_docs_field_udid')}</li>
                <li>${t('users.api_docs_field_task_type')}</li>
                <li>${t('users.api_docs_field_operator')}</li>
                <li>${t('users.api_docs_field_synced')}</li>
                <li>${t('users.api_docs_field_total')}</li>
                <li>${t('users.api_docs_field_from_to')}</li>
                <li>${t('users.api_docs_field_screenshot')}</li>
            </ul>
            <p class="settings-hint">${t('users.api_docs_retention')}</p>
        </div>
    `;
}
/** Delegated copy handler for the docs' example blocks (the key's own Copy is wired
 *  separately — it copies the unmasked value, which is never in the DOM). */
function wireDocsCopy(root) {
    root.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-copy]');
        if (!btn)
            return;
        void navigator.clipboard.writeText(btn.dataset['copy'] ?? '');
        btn.textContent = t('users.copied_check');
        setTimeout(() => { btn.textContent = t('users.copy_btn'); }, 1500);
    });
}
